import { useState, useEffect } from 'react';
import { Search, Eye, Store, X, Phone, Clock, User, Mail } from 'lucide-react';
import { BASE_URL } from '../Api/Config';
const Restaurant = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  // ✅ Derived filtered list - no state needed
  const filteredRestaurants = restaurants.filter((r) =>
    r.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getAuthToken = () => {
    return localStorage.getItem('token');
  };

  const fetchRestaurant = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await fetch(`${BASE_URL}/admin/restaurants`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRestaurants(data.restaurants || []);
      } else {
        console.error('Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      Yes: 'bg-green-100 text-green-800 border-green-200',
      No: 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleToggleFeatured = async (restaurantId, currentFeatured) => {
  try {
    setToggleLoading(true);
    setError('');
    
    const token = getAuthToken();
    if (!token) {
      setError('Authentication token not found. Please login again.');
      return;
    }
    
    // This should work with the URL parameter approach
    const response = await fetch(`${BASE_URL}/admin/feature-status/${restaurantId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    const data = await response.json();
    
    if (data.success) {
      setRestaurants(prevRestaurants =>
        prevRestaurants.map(r =>
          r._id === restaurantId ? { ...r, isFeatured: !currentFeatured } : r
        )
      );
      
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    } else {
      setError(data.message || 'Failed to update restaurant featured status');
    }
  } catch (err) {
    console.error('Error toggling restaurant featured status:', err);
    setError('Network error. Please check your connection and try again.');
  } finally {
    setToggleLoading(false);
  }
};


  useEffect(() => {

    fetchRestaurant();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <div className="p-2 lg:p-4">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading foods...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Restaurants</h1>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table View */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-semibold tracking-wider">
              <tr>
                <th className="px-6 py-3">ID</th>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Restaurant Contact</th>
                <th className="px-6 py-3">Is Active</th>
                <th className="px-6 py-3">Created At</th>
                <th className="px-6 py-3">Featured</th>
                <th className="px-6 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredRestaurants.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-6 text-gray-500">
                    No restaurants found
                  </td>
                </tr>
              ) : (
                filteredRestaurants.map((r, index) => (
                  <tr key={r._id || r.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4">{r.name}</td>
                    <td className="px-6 py-4">{r.phone}</td>
                    <td className="px-6 py-4">{getStatusBadge(r.isActive ? 'Yes' : 'No')}</td>
                    <td className="px-4 py-4 text-gray-600">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1 text-gray-400" />
                        {formatDate(r.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleFeatured(r._id, r.isFeatured)}
                        disabled={toggleLoading}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed
                        ${r.isFeatured ? 'bg-green-600 focus:ring-green-500' : 'bg-gray-300 focus:ring-gray-500'}
                      `}
                        title={r.isFeatured ? 'Click to unfeature' : 'Click to feature'}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ease-in-out 
                          ${r.isFeatured ? 'translate-x-6' : 'translate-x-1'}
                        `}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => setSelectedRestaurant(r)}
                        className="text-blue-600 hover:underline inline-flex items-center gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="sm:hidden space-y-4">
          {filteredRestaurants.length === 0 ? (
            <div className="text-center text-gray-500 py-4">No restaurants found</div>
          ) : (
            filteredRestaurants.map((r) => (
              <div key={r._id} className="bg-gray-50 rounded-md p-4 shadow-sm">
                <p><span className="font-semibold">ID:</span> #{r.id}</p>
                <p><span className="font-semibold">Name:</span> {r.name}</p>
                <p><span className="font-semibold">Phone:</span> {r.phone}</p>
                <p><span className="font-semibold">Location:</span> {r.location}</p>
                <p className="flex items-center gap-1 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  {new Date(r.createdAt).toLocaleString()}
                </p>
                <button
                  onClick={() => setSelectedRestaurant(r)}
                  className="text-blue-600 hover:underline inline-flex items-center gap-1 mt-2"
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal */}
      {selectedRestaurant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-md rounded-lg shadow-lg overflow-y-auto max-h-screen p-6 relative">
            <button
              onClick={() => setSelectedRestaurant(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Store className="w-5 h-5" />
              {selectedRestaurant.name} (Owner Details)
            </h2>
            <div className="space-y-2 text-sm text-gray-600">
              <p className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {selectedRestaurant.owner.name}
              </p>
              <p className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {selectedRestaurant.owner.phone}
              </p>
              <p className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {selectedRestaurant.owner.email}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Restaurant;
